import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageSelector from '../layout/LanguageSelector';

// ── Sub-components ────────────────────────────────────────────────────────────

const RadioGroup = ({ name, value, onChange, yesLabel, noLabel }) => (
  <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
    <label className="checkbox-label">
      <input
        type="radio"
        name={name}
        value="yes"
        checked={value === 'yes'}
        onChange={() => onChange('yes')}
        style={{ width: 'auto', marginRight: '8px' }}
      />
      <span>{yesLabel}</span>
    </label>
    <label className="checkbox-label">
      <input
        type="radio"
        name={name}
        value="no"
        checked={value === 'no'}
        onChange={() => onChange('no')}
        style={{ width: 'auto', marginRight: '8px' }}
      />
      <span>{noLabel}</span>
    </label>
  </div>
);


const CurrencyBlock = ({ index, data, onUpdate, onUpdateInstrument, onRemove, canRemove, t }) => {
  const instrumentKeys = [
    'contoCorrenteRemunerato',
    'depositiScadenza',
    'fondiMonetari',
    'polizzeAssicurative',
    'titoliDiStato'
  ];

  return (
    <div className="address-section" style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ margin: 0, color: 'var(--primary)', fontWeight: 600 }}>
          {t('assessment.currencyLabel')} {index}
        </h4>
        {canRemove && (
          <button type="button" className="btn btn-danger btn-sm" onClick={onRemove}>
            {t('assessment.buttons.removeCurrency')}
          </button>
        )}
      </div>

      <div className="form-group">
        <label>{t('assessment.fields.currency')}</label>
        <input
          type="text"
          value={data.currency}
          onChange={e => onUpdate('currency', e.target.value)}
          placeholder={t('assessment.fields.currencyPlaceholder')}
          style={{ maxWidth: '160px' }}
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>{t('assessment.fields.avgLiquidity')}</label>
          <input
            type="text"
            value={data.avgLiquidity}
            onChange={e => onUpdate('avgLiquidity', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>{t('assessment.fields.minimumLiquidity')}</label>
          <input
            type="text"
            value={data.minimumLiquidity}
            onChange={e => onUpdate('minimumLiquidity', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>{t('assessment.fields.seasonality')}</label>
        <textarea
          rows="2"
          value={data.seasonality}
          onChange={e => onUpdate('seasonality', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label>{t('assessment.fields.instruments')}</label>
        <div className="checkbox-group">
          {instrumentKeys.map(key => (
            <label key={key} className="checkbox-label" style={{ marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={data.instruments[key]}
                onChange={e => onUpdateInstrument(key, e.target.checked)}
                style={{ width: 'auto', marginRight: '10px' }}
              />
              <span>{t(`assessment.instruments.${key}`)}</span>
            </label>
          ))}
          <label className="checkbox-label" style={{ marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={data.instruments.altroChecked}
              onChange={e => onUpdateInstrument('altroChecked', e.target.checked)}
              style={{ width: 'auto', marginRight: '10px' }}
            />
            <span>{t('assessment.instruments.altro')}</span>
          </label>
          {data.instruments.altroChecked && (
            <div className="form-group" style={{ marginTop: '8px', marginBottom: 0 }}>
              <input
                type="text"
                value={data.instruments.altroText}
                onChange={e => onUpdateInstrument('altroText', e.target.value)}
                placeholder={t('assessment.instruments.altroPlaceholder')}
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>{t('assessment.fields.recentReturns')}</label>
        <textarea
          rows="2"
          value={data.recentReturns}
          onChange={e => onUpdate('recentReturns', e.target.value)}
        />
      </div>
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyInstruments = () => ({
  contoCorrenteRemunerato: false,
  depositiScadenza: false,
  fondiMonetari: false,
  polizzeAssicurative: false,
  titoliDiStato: false,
  altroChecked: false,
  altroText: ''
});

const emptyCurrency = (id) => ({
  id,
  currency: '',
  avgLiquidity: '',
  minimumLiquidity: '',
  seasonality: '',
  instruments: emptyInstruments(),
  recentReturns: ''
});

// ── Main Component ────────────────────────────────────────────────────────────

const Assessment = () => {
  const { t, i18n } = useTranslation();

  const [nextId, setNextId] = useState(2);
  const [currencies, setCurrencies] = useState([emptyCurrency(1)]);
  const [companyInfo, setCompanyInfo] = useState({ companyName: '', contactName: '' });
  const [currentBanks, setCurrentBanks] = useState('');
  const [formerBanks, setFormerBanks] = useState('');
  const [futureManagement, setFutureManagement] = useState({
    hasInvestmentPolicy: '',
    investmentPolicyNotes: '',
    canLockLiquidity: '',
    lockDetails: '',
    canOpenNewAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const addCurrency = () => {
    setCurrencies(prev => [...prev, emptyCurrency(nextId)]);
    setNextId(n => n + 1);
  };

  const removeCurrency = (id) => setCurrencies(prev => prev.filter(c => c.id !== id));

  const updateCurrency = (id, field, value) =>
    setCurrencies(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));

  const updateInstrument = (id, field, value) =>
    setCurrencies(prev => prev.map(c =>
      c.id === id ? { ...c, instruments: { ...c.instruments, [field]: value } } : c
    ));

const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (currencies.some(c => !c.currency.trim())) {
      setError(t('assessment.errorCurrencyRequired'));
      return;
    }

    setLoading(true);

    const payload = {
      ...companyInfo,
      language: i18n.language?.slice(0, 2),
      currencies: currencies.map(({ id, ...rest }) => rest),
      bankRelations: { currentBanks, formerBanks },
      futureManagement
    };

    try {
      await axios.post('/api/assessment', payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError(t('assessment.error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="logo-container">
            <img
              src="/logo512.png"
              alt="Euracle Logo"
              className="auth-logo"
              onError={e => e.target.style.display = 'none'}
            />
          </div>
          <div className="alert-success" style={{ textAlign: 'center', padding: '24px', fontSize: '1.05rem', borderRadius: '8px' }}>
            {t('assessment.success')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>

      <div className="auth-card" style={{ maxWidth: '1040px' }}>
        <div className="logo-container">
          <img
            src="/logo512.png"
            alt="Euracle Logo"
            className="auth-logo"
            onError={e => e.target.style.display = 'none'}
          />
        </div>

        <h2>{t('assessment.pageTitle')}</h2>
        <p className="auth-subtitle">{t('assessment.intro')}</p>

        <form onSubmit={handleSubmit}>

          {/* ── 0. Company info ── */}
          <div className="form-section">
            <h3>{t('assessment.sections.company')}</h3>
            <div className="form-group">
              <label>{t('assessment.fields.companyName')}</label>
              <input
                type="text"
                value={companyInfo.companyName}
                onChange={e => setCompanyInfo({ ...companyInfo, companyName: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('assessment.fields.contactName')}</label>
                <input
                  type="text"
                  value={companyInfo.contactName}
                  onChange={e => setCompanyInfo({ ...companyInfo, contactName: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* ── 1. Currencies ── */}
          <div className="form-section">
            <h3>{t('assessment.sections.liquidity')}</h3>
            {currencies.map((cur, idx) => (
              <CurrencyBlock
                key={cur.id}
                index={idx + 1}
                data={cur}
                onUpdate={(field, value) => updateCurrency(cur.id, field, value)}
                onUpdateInstrument={(field, value) => updateInstrument(cur.id, field, value)}
                onRemove={() => removeCurrency(cur.id)}
                canRemove={currencies.length > 1}
                t={t}
              />
            ))}
            <button type="button" className="btn btn-secondary" onClick={addCurrency}>
              + {t('assessment.buttons.addCurrency')}
            </button>
          </div>

          {/* ── 2. Bank relations ── */}
          <div className="form-section">
            <h3>{t('assessment.sections.banks')}</h3>
            <div className="form-group">
              <label>{t('assessment.fields.currentBanks')}</label>
              <textarea
                rows="3"
                value={currentBanks}
                onChange={e => setCurrentBanks(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label>{t('assessment.fields.formerBanks')}</label>
              <textarea
                rows="3"
                value={formerBanks}
                onChange={e => setFormerBanks(e.target.value)}
              />
            </div>
          </div>

          {/* ── 3. Future management ── */}
          <div className="form-section" style={{ borderBottom: 'none' }}>
            <h3>{t('assessment.sections.future')}</h3>
            <div className="form-group">
              <label>{t('assessment.fields.hasInvestmentPolicy')}</label>
              <RadioGroup
                name="hasInvestmentPolicy"
                value={futureManagement.hasInvestmentPolicy}
                onChange={v => setFutureManagement({ ...futureManagement, hasInvestmentPolicy: v })}
                yesLabel={t('assessment.yes')}
                noLabel={t('assessment.no')}
              />
              {futureManagement.hasInvestmentPolicy === 'yes' && (
                <div className="address-section" style={{ marginTop: '12px' }}>
                  <label>{t('assessment.fields.investmentPolicyNotes')}</label>
                  <textarea
                    rows="4"
                    value={futureManagement.investmentPolicyNotes}
                    onChange={e => setFutureManagement({ ...futureManagement, investmentPolicyNotes: e.target.value })}
                    placeholder={t('assessment.fields.investmentPolicyNotesPlaceholder')}
                  />
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label>{t('assessment.fields.canLockLiquidity')}</label>
              <RadioGroup
                name="canLockLiquidity"
                value={futureManagement.canLockLiquidity}
                onChange={v => setFutureManagement({ ...futureManagement, canLockLiquidity: v })}
                yesLabel={t('assessment.yes')}
                noLabel={t('assessment.no')}
              />
              {futureManagement.canLockLiquidity === 'yes' && (
                <div className="address-section" style={{ marginTop: '12px' }}>
                  <label>{t('assessment.fields.lockDetails')}</label>
                  <textarea
                    rows="3"
                    value={futureManagement.lockDetails}
                    onChange={e => setFutureManagement({ ...futureManagement, lockDetails: e.target.value })}
                  />
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label>{t('assessment.fields.canOpenNewAccount')}</label>
              <RadioGroup
                name="canOpenNewAccount"
                value={futureManagement.canOpenNewAccount}
                onChange={v => setFutureManagement({ ...futureManagement, canOpenNewAccount: v })}
                yesLabel={t('assessment.yes')}
                noLabel={t('assessment.no')}
              />
            </div>
          </div>

          {error && <div className="alert-danger" style={{ padding: '12px 15px', borderRadius: '4px', marginBottom: '20px' }}>{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
            style={{ marginTop: '24px', padding: '14px', fontSize: '1rem' }}
          >
            {loading ? t('assessment.buttons.submitting') : t('assessment.buttons.submit')}
          </button>

        </form>
      </div>
    </div>
  );
};

export default Assessment;
